import type { ChatMessage } from '@elara/client-core/models/ChatMessageModel';

import { useMemo, useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { useAppState, useAppStates, useChatsState, useMessageViewState, useUserProfileState } from '#states/hooks.js';
import {
  EventMessageCard,
  MessageAttachments,
  MessageDebugStatusBar,
  MessageFeedbackBar,
  ModelMessageCard,
  UserMessageCard,
} from '#ui/chat/index.js';
import { Button, MaterialIcon } from '#ui/index.js';

/**
 * Props for the MessageView component.
 */
interface MessageViewProps {
  msg: ChatMessage;
}

/**
 * Renders a single message within the chat interface.
 * @param props - The component props, containing the message to render.
 */
export function MessageView({ msg }: MessageViewProps) {
  const messageViewState = useMessageViewState();
  const chatsState = useChatsState();
  const userProfileState = useUserProfileState();
  const appState = useAppState();
  const { modelState } = useAppStates('modelState');
  const [actionInFlight, setActionInFlight] = useState<'delete' | 'resend' | undefined>(undefined);
  const state = useMemo(() => messageViewState.messageUiState(msg), [messageViewState, msg]);
  const isHighlighted = messageViewState.highlightedMessageId === state.viewId;
  const isLastMessage = chatsState.lastMessage?.id === msg.id;
  const hasTextContent = !!state.textContent && state.textContent.trim().length > 0;
  const showThinkingIndicator = msg.isModelMessage && isLastMessage && !hasTextContent;
  const shouldShowDeleteMessageAction =
    appState.debugMode && userProfileState.isPrivileged && !msg.deleted && !msg.isOptimistic && !msg.isStreaming;
  const shouldShowResendMessageAction =
    shouldShowDeleteMessageAction && msg.isUserMessage && chatsState.canResendMessage(msg.id);
  const canAdminDelete =
    userProfileState.isPrivileged && !msg.deleted && !msg.isOptimistic && !msg.isStreaming && !actionInFlight;
  const isDeleteMessageBusy = actionInFlight === 'delete';
  const shouldShowAdminDeleteButton = canAdminDelete || isDeleteMessageBusy;
  const canResendMessage = shouldShowResendMessageAction && !actionInFlight;

  const onDeleteMessage = async (): Promise<void> => {
    if (!canAdminDelete) return;
    const confirmed = window.confirm('Delete this message from chat history?');
    if (!confirmed) return;
    setActionInFlight('delete');
    try {
      await chatsState.deleteMessage(msg.id);
    } finally {
      setActionInFlight(undefined);
    }
  };

  const onResendMessage = async (): Promise<void> => {
    if (!canResendMessage) return;
    setActionInFlight('resend');
    try {
      await chatsState.resendMessage(msg.id, {
        modelId: modelState.modelOverride,
        promptVersion: modelState.promptVersionOverride,
        reasoningEffort: modelState.reasoningEffortOverride,
        skillVersions: modelState.skillVersionsOverride,
      });
    } finally {
      setActionInFlight(undefined);
    }
  };

  // Hide historical model messages that never produced any visible content. The last
  // message stays visible even when empty so the thinking indicator can render in it.
  // A stopped reply is kept even when empty and no longer last, so its "You stopped this
  // response." affordance survives subsequent sends (otherwise it vanishes on the next message).
  if (msg.isModelMessage && !appState.debugMode && !state.hasVisibleContent && !isLastMessage && !msg.wasStopped)
    return null;

  // Event messages (e.g. trip dates changed via UI) render as centered inline notices.
  if (msg.isEventMessage) {
    return (
      <div
        key={msg.id}
        id={`message-${state.viewId}`}
        className="msg-user-event [&_+_.msg-user-event]:-mt-4 [&_+_.msg-user]:-mt-6"
      >
        <EventMessageCard key={msg.id} msg={msg} />
      </div>
    );
  }

  return (
    <div
      key={msg.id}
      id={`message-${state.viewId}`}
      className={cn(
        'flex gap-3 p-2',
        'transition-all duration-200 animate-in fade-in-0',
        'transition-colors duration-500',
        'rounded-md -mx-2',
        isHighlighted && 'bg-primary/10',
        msg.isUserMessage ? 'justify-end msg-user' : 'justify-start',
      )}
    >
      <div
        className={cn('group/message relative flex flex-col gap-1 w-full max-w-2xl', msg.isUserMessage && 'items-end')}
      >
        {shouldShowAdminDeleteButton && (
          <Button
            type="button"
            variant="icon"
            size="icon-xs"
            tooltip="Delete message"
            aria-label="Delete message"
            className={cn(
              'pointer-events-none absolute top-1 z-10 rounded bg-muted/80 text-muted-foreground opacity-0 shadow-sm transition-opacity duration-150 hover:bg-destructive/20 hover:text-destructive group-hover/message:pointer-events-auto group-hover/message:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100',
              msg.isUserMessage ? 'right-1' : 'left-1',
              isDeleteMessageBusy && 'pointer-events-auto opacity-100',
            )}
            onClick={() => void onDeleteMessage()}
            isBusy={isDeleteMessageBusy}
            disabled={!!actionInFlight}
          >
            <MaterialIcon icon="delete" size="xxs" />
          </Button>
        )}

        {/* Attachments */}
        {state.attachments && state.attachments.length > 0 && (
          <MessageAttachments attachments={state.attachments} messageId={msg.id} />
        )}

        {/* Message Body */}
        {msg.isUserMessage ? (
          <UserMessageCard msg={msg} state={state} />
        ) : (
          <ModelMessageCard msg={msg} state={state} showThinkingIndicator={showThinkingIndicator} />
        )}

        {/* Message Feedback */}
        {!msg.isUserMessage && msg.textContent && <MessageFeedbackBar msg={msg} state={state} />}

        {appState.debugMode && userProfileState.isPrivileged && (
          <MessageDebugStatusBar
            msg={msg}
            state={state}
            showDeleteMessageAction={shouldShowDeleteMessageAction}
            isDeleteMessageBusy={isDeleteMessageBusy}
            onDeleteMessage={() => void onDeleteMessage()}
            showResendMessageAction={shouldShowResendMessageAction}
            isResendMessageBusy={actionInFlight === 'resend'}
            onResendMessage={() => void onResendMessage()}
          />
        )}
      </div>
    </div>
  );
}
